import { Star } from "lucide-react";
import { motion } from "framer-motion";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from "@/components/ui/carousel";

const container = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1
    }
  }
};

const item = {
  hidden: { opacity: 0, y: 20 },
  show: { 
    opacity: 1, 
    y: 0,
    transition: {
      duration: 0.6
    }
  }
};

export const TestimonialsSection = () => {
  const testimonials = [
    {
      type: "Student",
      quote: "The things taught in these sessions were really useful information, and a good refresher, since it's been a long time since I coded. It was a fun and useful experience!",
      author: "Arjun Nair",
      details: "9th grade student",
      rating: 4
    },
    {
      type: "Parent",
      quote: "Thank you so much for teaching this course! [Student] likes it very much and learned a lot! Appreciate your great help and hope to have more chances to work with you in the future!",
      author: "Xuejun Zhu",
      details: "Parent of a 4th grader",
      rating: 5
    },
    {
      type: "Parent",
      quote: "Thank you so much for hosting the session for [Student]; I really appreciate it. He had a very good time.",
      author: "Anonymous",
      details: "Parent of a 5th grader",
      rating: 5
    },
    {
      type: "Student",
      quote: "This class was very informative and engaging. It was fun to be with a young teacher, and being on Zoom helped so we could share screens. I would love if the class was longer so we could all have our questions answered. I found using Koda Studio super dynamic and easy to access. Thank you for putting so much effort into this. I would love to join again, possibly for a higher-level class.",
      author: "Nila",
      details: "Python student",
      rating: 5
    },
    {
      type: "Student",
      quote: "It was really, really fun and informative. Taran was a great teacher, and Koda Studio elevated the experience. I highly recommend it.",
      author: "Samanyu",
      details: "Python student",
      rating: 5
    },
    {
      type: "Student",
      quote: "I thought it was very informative and helpful since I knew essentially nothing about code prior. Thank you, I needed a way to learn how to code, and this was the perfect way, especially because you eased the difficulty.",
      author: "Grey",
      details: "Python student",
      rating: 5
    },
    {
      type: "Student",
      quote: "I really enjoyed this class. It was really fun and educational. It taught me a lot and encouraged me to really start learning how to code. I highly recommend this class.",
      author: "Mayukha",
      details: "Python student",
      rating: 5
    },
    {
      type: "Student",
      quote: "The class was really good! I liked how it taught us AI in a way that was fun and entertaining. The games were really fun and the model part was the best. I learned a lot, but I would recommend focusing more on a wide variety of topics rather than one singular topic. All topics were understandable and I liked learning about them.",
      author: "Shreyan",
      details: "AI session student",
      rating: 4
    }
  ];

  return (
    <section className="relative py-24 overflow-hidden bg-background">
      <div className="absolute inset-0 bg-gradient-to-br from-primary/5 to-background/80">
        <div className="absolute inset-0 bg-grid-white/5 [mask-image:radial-gradient(ellipse_at_center,transparent_0%,hsl(var(--background))_80%)]"></div>
      </div>
      
      <div className="container mx-auto px-4 relative z-10">
        <motion.div 
          variants={container}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true }}
          className="max-w-5xl mx-auto text-center space-y-16"
        >
          {/* Header */}
          <motion.div variants={item} className="space-y-6">
            <h2 className="text-4xl sm:text-5xl lg:text-6xl font-light tracking-tight text-foreground">
              Proven <span className="text-primary">Excellence</span>
            </h2>
            <motion.div 
              variants={item}
              className="h-0.5 bg-gradient-to-r from-primary/30 to-transparent w-24 mx-auto"
            />
            <p className="text-lg sm:text-xl text-muted-foreground leading-relaxed max-w-3xl mx-auto">
              Hear from the students and families who've experienced Byte-Sized Python firsthand.
            </p>
          </motion.div>

          {/* Testimonials Carousel */}
          <motion.div variants={item} className="relative px-10 sm:px-12">
            <Carousel
              opts={{ align: "start", loop: true, slidesToScroll: "auto" }}
              className="w-full"
            >
              <CarouselContent className="-ml-4">
                {testimonials.map((testimonial, index) => (
                  <CarouselItem
                    key={index}
                    className="pl-4 basis-full sm:basis-1/2 lg:basis-1/3"
                  >
                    <div className="group h-full bg-background/80 backdrop-blur-sm rounded-xl p-8 border border-foreground/10 hover:border-primary/20 transition-colors flex flex-col">
                      <div className="flex-1 space-y-6">
                        <div className="flex items-center justify-between">
                          <span className="bg-primary/10 text-primary px-3 py-1 rounded-full text-xs font-medium">
                            {testimonial.type}
                          </span>
                          <div className="flex items-center space-x-1">
                            {[...Array(5)].map((_, i) => (
                              <Star
                                key={i}
                                className={
                                  i < testimonial.rating
                                    ? "w-4 h-4 fill-primary text-primary"
                                    : "w-4 h-4 text-primary/30"
                                }
                              />
                            ))}
                          </div>
                        </div>

                        <blockquote className="text-foreground leading-relaxed text-left">
                          “{testimonial.quote}”
                        </blockquote>
                      </div>

                      <div className="pt-6 mt-6 border-t border-foreground/10 text-left">
                        <p className="font-semibold text-foreground">
                          {testimonial.author}
                        </p>
                        <p className="text-sm text-muted-foreground mt-1">
                          {testimonial.details}
                        </p>
                      </div>
                    </div>
                  </CarouselItem>
                ))}
              </CarouselContent>
              <CarouselPrevious className="flex" />
              <CarouselNext className="flex" />
            </Carousel>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};